import { IFolderRepository } from '../../domain/repositories/IFolderRepository';
import { Folder } from '../../domain/entities/Folder';
import { ConfigManager } from '../../application/config/Config';
import { LoggerFactory } from '../../application/logging/Logger';
import { InternalServerError } from '../../application/errors/AppError';
import { FolderModelString } from "../../types/index";
/**
 * API-based Folder Repository Implementation
 * Follows Repository Pattern and Dependency Inversion Principle
 */
export class FolderRepository implements IFolderRepository {
  private readonly config = ConfigManager.getInstance().getApiConfig();
  private readonly logger = LoggerFactory.getLogger();

  /**
   * Creates or updates a folder for a coach and swimmer
   * @param coachId - The unique identifier of the coach
   * @param swimmerId - The unique identifier of the swimmer
   * @param folderId - The folder identifier (local ID for new folders)
   * @param name - The name of the folder
   * @param description - The description of the folder
   * @param isFavorite - Whether the folder is marked as favorite
   * @returns FolderModelString with folder information and sync status
   * @throws Error if the operation fails
   */
  async manageFolder(coachId: number, swimmerId: number, folderId: string, name: string, description: string, isFavorite: boolean): Promise<FolderModelString> {
    const payload = {
      coach_id: coachId,
      swimmer_id: swimmerId,
      name,
      description,
      local_folder_id: folderId,
      isFavorite: isFavorite ? 1 : 0,
    };

    const formBody = new URLSearchParams(payload as any).toString();

    try {
      const response = await fetch(`${this.config.baseUrl}/folderpractice/manage_folder`, {
        method: "POST",
        headers: {
          "X-API-Key": this.config.apiKey,
          "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody,
      });

      const rawText = await response.text();

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status} - ${rawText}`);
      }

      let data: any = {};
      try {
        data = JSON.parse(rawText);
      } catch {
        this.logger.warn("Response was not JSON, using fallback object", { folderId, name });
      }

      const folder: FolderModelString = {
        folder_id: data.folder_id?.toString() ?? folderId,
        name: data.name ?? name,
        description: data.description ?? description,
        createdAt: data.createdAt ?? new Date().toISOString(),
        coach_id: data.coach_id?.toString() ?? coachId.toString(),
        swimmer_id: data.swimmer_id?.toString() ?? swimmerId.toString(),
        isFavorite: data.isFavorite?.toString() ?? (isFavorite ? "1" : "0"),
        SyncStatus: "Synced",
      };

      return folder;
    } catch (error) {
      this.logger.error("Error managing folder", error as Error, { coachId, swimmerId, folderId, name });

      return {
        folder_id: folderId,
        name,
        description,
        createdAt: new Date().toISOString(),
        coach_id: coachId.toString(),
        swimmer_id: swimmerId.toString(),
        isFavorite: isFavorite ? "1" : "0",
        SyncStatus: "Unsynced",
      };
    }
  }

  /**
   * Retrieves all folders, practices, and laps for a specific swimmer
   * @param userId - The unique identifier of the swimmer
   * @returns Object containing folders, practices, laps arrays, and pagination info
   * @throws InternalServerError if the fetch operation fails
   */
  async getAllFolders(userId: number): Promise<{
  folders: FolderModelString[];
  practices: any[];
  laps: any[];
  totalPages: number;
  total: number;
}> {
    try {
      const url = `${this.config.baseUrl}/folderpractice/folder_all_data_by_swimmer/${userId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      const rawText = await response.text();
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status} - ${rawText}`);
      }

      let data: any;
      try {
        data = JSON.parse(rawText);
      } catch {
        this.logger.warn("Response is not valid JSON", { userId });
        data = {};
      }

      // Make sure folders exist
      const folderArray = data?.data?.folders || [];
      this.logger.debug('Folder API data received', { userId, folderCount: folderArray.length });
      const practiceArray = data?.data?.practices || [];
      const lapArray = data?.data?.laps || [];
      
      const folders: FolderModelString[] = folderArray.map((f: any) => ({
        folder_id: f.folder_id?.toString() ?? "",
        name: f.name ?? "Untitled Folder",
        description: f.description ?? "",
        createdAt: f.createdAt ?? new Date().toISOString(),
        coach_id: f.coach_id?.toString() ?? userId.toString(),
        swimmer_id: f.swimmer_id?.toString() ?? "",
        isFavorite: f.isFavorite?.toString() ?? "0",
        SyncStatus: "Synced",
      }));

      return {
        folders,
        practices: practiceArray,
        laps: lapArray,
        totalPages: data?.pagination?.total_pages ?? 1,
        total: data?.pagination?.total_items ?? folderArray.length,
      };
    } catch (error) {
      this.logger.error("Error fetching folders", error as Error, { userId });
      throw new InternalServerError('Failed to fetch folders', { userId, cause: error });
    }
  }

    async getAllFoldersPaginated(userId: number, pageNumber: number,pageLimit: number): Promise<{
  folders: FolderModelString[];
    practices: any[];
    laps: any[];
    totalPages: number;
    total: number;
  }> {
    try {
      const url = `${this.config.baseUrl}/folderpractice/folder_all_data_by_swimmer_paginated/${userId}?page=${pageNumber}&limit=${pageLimit}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      const rawText = await response.text();
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status} - ${rawText}`);
      }

      let data: any;
      try {
        data = JSON.parse(rawText);
      } catch {
        this.logger.warn("Response is not valid JSON", { userId });
        data = {};
      }

      // Make sure folders exist
      const folderArray = data?.data?.folders || [];
      const practiceArray = data?.data?.practices || [];
      const lapArray = data?.data?.laps || [];

      const folders: FolderModelString[] = folderArray.map((f: any) => ({
        folder_id: f.folder_id?.toString() ?? "",
        name: f.name ?? "Untitled Folder",
        description: f.description ?? "",
        createdAt: f.createdAt ?? new Date().toISOString(),
        coach_id: f.coach_id?.toString() ?? userId.toString(),
        swimmer_id: f.swimmer_id?.toString() ?? "",
        isFavorite: f.isFavorite?.toString() ?? "0",
        SyncStatus: "Synced",
      }));

      return {
         folders,
        practices: practiceArray,
        laps: lapArray,
        totalPages: data?.pagination?.total_pages ?? 1,
        total: data?.pagination?.total_items ?? folderArray.length,
      };
    } catch (error) {
      this.logger.error("Error fetching paginated folders", error as Error, { userId, pageNumber, pageLimit });
      throw new InternalServerError('Failed to fetch paginated folders', { userId, pageNumber, pageLimit, cause: error });
    }
  }

  /**
   * Retrieves all practices associated with a specific folder
   * @param folderId - The unique identifier of the folder
   * @returns Array of practice objects with id, name, description, date, practice_time, yardage, and type
   * @throws InternalServerError if the fetch operation fails
   */
  async getPracticesByFolderId(folderId: number): Promise<Array<{
    id: string;
    name: string;
    description: string;
    date: string;
    practice_time: string;
    yardage: number;
    type: string;
  }>> {
  try {
    const url = `${this.config.baseUrl}/folderpractice/folder_practice_list_by_folder_id/${folderId}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
        "X-API-Key": this.config.apiKey,
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    const rawText = await response.text();
    if (!response.ok) throw new Error(`HTTP ${response.status} - ${rawText}`);

    const data = JSON.parse(rawText);

    this.logger.debug("API folder data received", { folderId, dataKeys: Object.keys(data || {}) });

    const folderItems = Array.isArray(data?.data) ? data.data : [];

    const practices = folderItems.map((item: any) => {
      const p = item.practice_model || {};
      return {
        id: p.folder_practice_model_id?.toString() ?? "",
        name: p.name ?? "Untitled Practice",
        description: p.description ?? "",
        date: p.practice_date ?? new Date().toISOString(),
        practice_time: p.practice_time ?? "",
        yardage: p.total_yardage ?? 0,
        type: "workout",
      };
    });

    this.logger.debug("Extracted practices", { folderId, practiceCount: practices.length });
    return practices;
  } catch (err) {
    this.logger.error("Error fetching practices by folder", err as Error, { folderId });
    throw new InternalServerError('Failed to fetch practices by folder', { folderId, cause: err });
  }
}

  /**
   * Retrieves a folder record by its ID
   * @param id - The unique identifier of the folder record
   * @returns Folder domain entity or null if not found
   * @throws InternalServerError if the fetch operation fails
   */
  async findById(id: string): Promise<Folder | null> {
    try {
      const response = await fetch(`${this.config.baseUrl}/folder-practices/${id}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new InternalServerError('Failed to fetch folder');
      }

      const data = await response.json();
      return Folder.fromData(data);
    } catch (error) {
      this.logger.error('Failed to find folder by ID', error as Error, { folderId: id });
      throw error;
    }
  }

  async findByUserId(userId: string): Promise<Folder[]> {
    try {
      const response = await fetch(`${this.config.baseUrl}/folder-practices?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch user folders');
      }

      const data = await response.json();
      return data.folders?.map((folder: unknown) => Folder.fromData(folder as {
        id: string;
        user_id: string;
        name: string;
        description: string;
        color: string;
        is_public: boolean;
        practice_count?: number;
        created_at?: string;
        updated_at?: string;
      })) || [];
    } catch (error) {
      this.logger.error('Failed to find folders by user ID', error as Error, { userId });
      throw error;
    }
  }

  async findPublic(): Promise<Folder[]> {
    try {
      const response = await fetch(`${this.config.baseUrl}/folder-practices/public`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch public folders');
      }

      const data = await response.json();
      return data.folders?.map((folder: unknown) => Folder.fromData(folder as {
        id: string;
        user_id: string;
        name: string;
        description: string;
        color: string;
        is_public: boolean;
        practice_count?: number;
        created_at?: string;
        updated_at?: string;
      })) || [];
    } catch (error) {
      this.logger.error('Failed to find public folders', error as Error);
      throw error;
    }
  }

  /**
   * Creates a new folder record
   * @param folder - The Folder domain entity to save
   * @returns Created Folder domain entity with server-generated ID
   * @throws InternalServerError if the save operation fails
   */
  async save(folder: Folder): Promise<Folder> {
    try {
      const response = await fetch(`${this.config.baseUrl}/folder-practices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(folder.toJSON())
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to save folder');
      }

      const data = await response.json();
      return Folder.fromData(data);
    } catch (error) {
      this.logger.error('Failed to save folder', error as Error, { folderId: folder.id });
      throw error;
    }
  }

  /**
   * Updates an existing folder record
   * @param folder - The Folder domain entity with updated data
   * @returns Updated Folder domain entity
   * @throws InternalServerError if the update operation fails
   */
  async update(folder: Folder): Promise<Folder> {
    try {
      const response = await fetch(`${this.config.baseUrl}/folder-practices/${folder.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(folder.toJSON())
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to update folder');
      }

      const data = await response.json();
      return Folder.fromData(data);
    } catch (error) {
      this.logger.error('Failed to update folder', error as Error, { folderId: folder.id });
      throw error;
    }
  }

  /**
   * Deletes a folder record by its ID
   * @param id - The unique identifier of the folder record to delete
   * @throws InternalServerError if the delete operation fails
   */
  async delete(id: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/folder-practices/${id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to delete folder');
      }
    } catch (error) {
      this.logger.error('Failed to delete folder', error as Error, { folderId: id });
      throw error;
    }
  }

  async deleteFolderPractice(id: string): Promise<void> {
  try {
    const formData = new FormData();
    formData.append("folder_practice_model_id", id);

    const response = await fetch(`${this.config.baseUrl}/folderpractice/remove_folder_practice_model`, {
      method: "POST",
      headers: {
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to delete practice (status: ${response.status})`);
    }

    const text = await response.text();
    if (text) {
      try {
        const data = JSON.parse(text);
        this.logger.debug("Delete practice response", { practiceId: id, response: data });
      } catch {
        this.logger.debug("Delete practice response (non-JSON)", { practiceId: id, response: text });
      }
    }
  } catch (error) {
    this.logger.error("Failed to delete practice", error as Error, { practiceId: id });
    throw error;
  }
}

  async deleteFolder(folderId: number): Promise<void> {
  try {
    const formData = new FormData();
    formData.append("folder_id", folderId.toString());

    const response = await fetch(`${this.config.baseUrl}/folderpractice/remove_folder`, {
      method: "POST", 
      headers: {
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to delete folder (status: ${response.status})`);
    }

    const text = await response.text();
    if (text) {
      try {
        const data = JSON.parse(text);
        this.logger.debug("Delete folder response", { folderId, response: data });
      } catch {
        this.logger.debug("Delete folder response (non-JSON)", { folderId, response: text });
      }
    }

  } catch (error) {
    this.logger.error("Failed to delete folder", error as Error, { folderId });
    throw error;
  }
}

  async addPracticeToFolder(folderId: string, practiceId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/folder-practices/${folderId}/practices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ practice_id: practiceId })
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to add practice to folder');
      }
    } catch (error) {
      this.logger.error('Failed to add practice to folder', error as Error, { folderId, practiceId });
      throw error;
    }
  }

  async removePracticeFromFolder(folderId: string, practiceId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/folder-practices/${folderId}/practices/${practiceId}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to remove practice from folder');
      }
    } catch (error) {
      this.logger.error('Failed to remove practice from folder', error as Error, { folderId, practiceId });
      throw error;
    }
  }

  async movePracticeToFolder(practiceId: string, fromFolderId: string, toFolderId: string): Promise<void> {
    try {
      // Remove from source folder
      await this.removePracticeFromFolder(fromFolderId, practiceId);
      
      // Add to destination folder
      await this.addPracticeToFolder(toFolderId, practiceId);
    } catch (error) {
      this.logger.error('Failed to move practice between folders', error as Error, { 
        practiceId, 
        fromFolderId, 
        toFolderId 
      });
      throw error;
    }
  }

  async search(userId: string, query: string): Promise<Folder[]> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        search: query
      });

      const response = await fetch(`${this.config.baseUrl}/folder-practices/search?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to search folders');
      }

      const data = await response.json();
      return data.folders?.map((folder: unknown) => Folder.fromData(folder as {
        id: string;
        user_id: string;
        name: string;
        description: string;
        color: string;
        is_public: boolean;
        practice_count?: number;
        created_at?: string;
        updated_at?: string;
      })) || [];
    } catch (error) {
      this.logger.error('Failed to search folders', error as Error, { userId, query });
      throw error;
    }
  }

  async findByFilters(filters: {
    userId: string;
    isPublic?: boolean;
    color?: string;
  }): Promise<Folder[]> {
    try {
      const params = new URLSearchParams({ user_id: filters.userId });
      
      if (filters.isPublic !== undefined) {
        params.append('is_public', filters.isPublic.toString());
      }
      if (filters.color) {
        params.append('color', filters.color);
      }

      const response = await fetch(`${this.config.baseUrl}/folder-practices/filter?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to filter folders');
      }

      const data = await response.json();
      return data.folders?.map((folder: unknown) => Folder.fromData(folder as {
        id: string;
        user_id: string;
        name: string;
        description: string;
        color: string;
        is_public: boolean;
        practice_count?: number;
        created_at?: string;
        updated_at?: string;
      })) || [];
    } catch (error) {
      this.logger.error('Failed to filter folders', error as Error, { filters });
      throw error;
    }
  }

  async getFolderStats(folderId: string): Promise<{
    practiceCount: number;
    totalDistance: number;
    totalDuration: number;
    averagePace: number;
  }> {
    try {
      const response = await fetch(`${this.config.baseUrl}/folder-practices/${folderId}/stats`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch folder statistics');
      }

      const data = await response.json();
      return {
        practiceCount: data.practice_count || 0,
        totalDistance: data.total_distance || 0,
        totalDuration: data.total_duration || 0,
        averagePace: data.average_pace || 0
      };
    } catch (error) {
      this.logger.error('Failed to get folder statistics', error as Error, { folderId });
      throw error;
    }
  }

  async findPaginated(userId: string, page: number, limit: number): Promise<{
    folders: Folder[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        page: page.toString(),
        limit: limit.toString()
      });

      const response = await fetch(`${this.config.baseUrl}/folder-practices?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch paginated folders');
      }

      const data = await response.json();
      return {
        folders: data.folders?.map((folder: unknown) => Folder.fromData(folder as {
          id: string;
          user_id: string;
          name: string;
          description: string;
          color: string;
          is_public: boolean;
          practice_count?: number;
          created_at?: string;
          updated_at?: string;
        })) || [],
        total: data.total || 0,
        page: data.page || page,
        limit: data.limit || limit,
        totalPages: data.total_pages || 0
      };
    } catch (error) {
      this.logger.error('Failed to get paginated folders', error as Error, { 
        userId, 
        page, 
        limit 
      });
      throw error;
    }
  }
}
